import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { documentoAPI, historialAPI } from '../lib/api';
import { compressImage } from '../lib/compressImage';
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../lib/utils';
import { Layout } from '../components/Layout';
import { StatusBadge } from '../components/StatusBadge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Skeleton } from '../components/ui/skeleton';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
    FileText, Truck, Building, User, Clock, Package, ArrowLeft,
    CheckCircle, XCircle, AlertTriangle, History, Printer, QrCode,
    Image as ImageIcon, Download, Eye, X, FileImage, Upload, Loader2, IdCard, Hash,
} from 'lucide-react';

const MOTIVOS_RECHAZO_PI = [
    "Documento incompleto / ilegible",
    "Carga no coincide con guía",
    "Vehículo no autorizado",
    "Conductor sin documentación",
    "Sello de seguridad violado",
    "Faltan elementos de protección",
    "Otro (especificar en observación)",
];

export default function DocumentoDetallePage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [documento, setDocumento] = useState(null);
    const [historial, setHistorial] = useState([]);
    const [loading, setLoading] = useState(true);
    const [previewImage, setPreviewImage] = useState(null);

    // Acciones por rol
    const [actionType, setActionType] = useState(null); // 'aprobar' | 'rechazar_gerente' | 'despachar' | 'rechazar_pi'
    const [observacion, setObservacion] = useState('');
    const [motivosRechazo, setMotivosRechazo] = useState([]);
    const [processing, setProcessing] = useState(false);

    // Subir documento firmado por aprobador
    const fileInputRef = useRef(null);
    const [uploadingFirma, setUploadingFirma] = useState(false);

    useEffect(() => {
        loadData();
    }, [id]);

    const loadData = async () => {
        setLoading(true);
        try {
            const [docRes, histRes] = await Promise.all([
                documentoAPI.getById(id),
                historialAPI.getAll(id),
            ]);
            setDocumento(docRes.data);
            setHistorial(histRes.data);
        } catch (err) {
            console.error('Error loading documento:', err);
        } finally {
            setLoading(false);
        }
    };

    // Permisos por rol (memoizados)
    const permisos = useMemo(() => {
        const rol = (user && user.rol) || '';
        const esGer = rol === 'gerente' || rol === 'admin' || rol === 'superadmin';
        const esSeg = rol === 'seguridad' || rol === 'admin' || rol === 'superadmin';
        return {
            esGerente: esGer,
            esSeguridad: esSeg,
            puedeAprobar: esGer && documento && documento.estado === 'pendiente',
            puedeDespachar: esSeg && documento && documento.estado === 'autorizado',
            puedeSubirFirma: esGer && documento && documento.estado === 'pendiente',
        };
    }, [user, documento]);
    const { puedeAprobar, puedeDespachar, puedeSubirFirma } = permisos;

    const handleSubirFirma = async (ev) => {
        const file = ev.target.files?.[0];
        if (!file) return;
        setUploadingFirma(true);
        try {
            // Imágenes: comprimir en navegador. PDFs: enviar tal cual (se comprimen en backend).
            const fileToUpload = await compressImage(file);
            const formData = new FormData();
            formData.append('file', fileToUpload);
            const r = await documentoAPI.uploadFirmaAprobador(id, formData);
            toast.success('Documento firmado adjuntado');
            // Refrescar
            setDocumento(prev => ({ ...prev, documento_firma_aprobador_url: r.data.url }));
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al subir el documento');
        } finally {
            setUploadingFirma(false);
            if (ev.target) ev.target.value = '';
        }
    };

    const openAccion = (type) => {
        setActionType(type);
        setObservacion('');
        setMotivosRechazo([]);
    };

    const closeAccion = () => {
        setActionType(null);
        setObservacion('');
        setMotivosRechazo([]);
    };

    const toggleMotivo = (m) => {
        setMotivosRechazo(prev => prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m]);
    };

    const ejecutarAccion = async () => {
        if (!documento) return;
        const esRechazo = actionType === 'rechazar_gerente' || actionType === 'rechazar_pi';
        if (esRechazo && !observacion.trim()) {
            toast.error('Debe indicar el motivo del rechazo en observación');
            return;
        }
        if (actionType === 'rechazar_pi' && motivosRechazo.length === 0) {
            toast.error('Seleccione al menos un motivo de rechazo');
            return;
        }
        setProcessing(true);
        try {
            if (actionType === 'aprobar' || actionType === 'rechazar_gerente') {
                await documentoAPI.aprobar(documento.id, {
                    documento_id: documento.id,
                    estado: actionType === 'aprobar' ? 'autorizado' : 'rechazado',
                    observacion: observacion || null,
                });
                toast.success(actionType === 'aprobar' ? 'Guía autorizada' : 'Guía rechazada');
            } else {
                await documentoAPI.despachar(documento.id, {
                    documento_id: documento.id,
                    estado: actionType === 'despachar' ? 'despachado' : 'rechazado',
                    motivos_rechazo: actionType === 'rechazar_pi' ? motivosRechazo : [],
                    observacion: observacion || null,
                });
                toast.success(actionType === 'despachar' ? 'Guía despachada' : 'Guía rechazada en P.I.');
            }
            closeAccion();
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al procesar');
        } finally {
            setProcessing(false);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="space-y-6">
                    <Skeleton className="h-10 w-64" />
                    <Skeleton className="h-96" />
                </div>
            </Layout>
        );
    }

    if (!documento) {
        return (
            <Layout>
                <div className="text-center py-16">
                    <FileText className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                    <h2 className="font-heading text-xl font-bold">Documento no encontrado</h2>
                    <Button onClick={() => navigate(-1)} className="mt-4" variant="outline">
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Volver
                    </Button>
                </div>
            </Layout>
        );
    }

    const getHistoryIcon = (accion) => {
        if (accion.includes('CREACIÓN')) return <FileText className="h-4 w-4" />;
        if (accion.includes('AUTORIZADO')) return <CheckCircle className="h-4 w-4 text-success" />;
        if (accion.includes('RECHAZADO') || accion.includes('rechazado')) return <XCircle className="h-4 w-4 text-destructive" />;
        if (accion.includes('DESPACHADO')) return <Truck className="h-4 w-4 text-success" />;
        return <Clock className="h-4 w-4" />;
    };

    return (
        <Layout>
            <div className="max-w-4xl mx-auto space-y-6 animate-fade-in">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate(-1)}
                            className="border-2"
                            data-testid="back-btn"
                        >
                            <ArrowLeft className="h-5 w-5" />
                        </Button>
                        <div>
                            <h1 className="font-heading text-2xl md:text-3xl font-black uppercase tracking-tight">
                                {documento.folio}
                            </h1>
                            <StatusBadge estado={documento.estado} size="lg" />
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        {puedeAprobar && (
                            <>
                                <Button onClick={() => openAccion('aprobar')} className="bg-success hover:bg-success/90" data-testid="btn-detalle-aprobar">
                                    <CheckCircle className="h-4 w-4 mr-1" /> Aprobar
                                </Button>
                                <Button onClick={() => openAccion('rechazar_gerente')} variant="destructive" data-testid="btn-detalle-rechazar-gerente">
                                    <XCircle className="h-4 w-4 mr-1" /> Rechazar
                                </Button>
                            </>
                        )}
                        {puedeDespachar && (
                            <>
                                <Button onClick={() => openAccion('despachar')} className="bg-success hover:bg-success/90" data-testid="btn-detalle-despachar">
                                    <CheckCircle className="h-4 w-4 mr-1" /> Despachar
                                </Button>
                                <Button onClick={() => openAccion('rechazar_pi')} variant="destructive" data-testid="btn-detalle-rechazar-pi">
                                    <XCircle className="h-4 w-4 mr-1" /> Rechazar
                                </Button>
                            </>
                        )}
                        <Button variant="outline" className="border-2" data-testid="print-btn" onClick={() => window.print()}>
                            <Printer className="h-4 w-4 mr-2" />
                            Imprimir
                        </Button>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Main Info */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Basic Info */}
                        <Card className="border-2">
                            <CardHeader className="card-industrial-header">
                                <CardTitle className="flex items-center gap-2">
                                    <FileText className="h-5 w-5" />
                                    Información General
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Tipo de documento</p>
                                        <p className="font-medium capitalize">{documento.tipo_documento_nombre || documento.tipo_documento || documento.tipo || '—'}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1 flex items-center gap-1">
                                            <Hash className="h-3 w-3" /> N° guía / factura
                                        </p>
                                        <p className="font-mono font-bold text-lg" data-testid="detalle-folio-fisico">{documento.folio_fisico || '—'}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Fecha creación</p>
                                        <p className="font-medium">{formatDate(documento.created_at)}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Última actualización</p>
                                        <p className="font-medium">{formatDate(documento.updated_at)}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Solicitante</p>
                                        <p className="font-medium">{documento.usuario_solicitante_nombre}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Aprobador asignado</p>
                                        <p className="font-medium">{documento.aprobador_nombre || '—'}</p>
                                    </div>
                                </div>
                                {documento.descripcion && (
                                    <div className="mt-4 pt-4 border-t border-border">
                                        <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Descripción / Observaciones</p>
                                        <p className="text-sm whitespace-pre-wrap">{documento.descripcion}</p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Conductor */}
                        <Card className="border-2">
                            <CardHeader className="card-industrial-header">
                                <CardTitle className="flex items-center gap-2">
                                    <User className="h-5 w-5" /> Conductor
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Nombre</p>
                                        <p className="font-medium">{documento.nombre_conductor || '—'}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1 flex items-center gap-1">
                                            <IdCard className="h-3 w-3" /> RUT
                                        </p>
                                        <p className="font-mono font-medium">{documento.rut_conductor || '—'}</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Vehicle & Destination */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Card className="border-2">
                                <CardHeader className="card-industrial-header">
                                    <CardTitle className="flex items-center gap-2">
                                        <Truck className="h-5 w-5" />
                                        Vehículo
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="p-6 space-y-3">
                                    <div>
                                        <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Patente tracto</p>
                                        <p className="font-mono text-2xl font-bold text-primary">{documento.patente_tracto || documento.patente || '—'}</p>
                                    </div>
                                    {documento.patente_semiremolque && (
                                        <div>
                                            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Semirremolque</p>
                                            <p className="font-mono text-lg font-semibold">{documento.patente_semiremolque}</p>
                                        </div>
                                    )}
                                    {documento.vehiculo_info && (documento.vehiculo_info.marca || documento.vehiculo_info.modelo) && (
                                        <p className="text-sm text-muted-foreground">
                                            {documento.vehiculo_info.marca} {documento.vehiculo_info.modelo}
                                        </p>
                                    )}
                                </CardContent>
                            </Card>

                            <Card className="border-2">
                                <CardHeader className="card-industrial-header">
                                    <CardTitle className="flex items-center gap-2">
                                        <Building className="h-5 w-5" />
                                        Empresa destino
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="p-6 space-y-3">
                                    <div>
                                        <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Razón social</p>
                                        <p className="font-medium">{documento.razon_social || documento.destino_info?.razon_social || '—'}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">RUT</p>
                                        <p className="font-mono text-sm">{documento.rut_empresa || documento.rut_destino || '—'}</p>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Items */}
                        {documento.items && documento.items.length > 0 && (
                            <Card className="border-2">
                                <CardHeader className="card-industrial-header">
                                    <CardTitle className="flex items-center gap-2">
                                        <Package className="h-5 w-5" />
                                        Detalle de Carga ({documento.items.length} items)
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="p-0">
                                    <div className="divide-y divide-border">
                                        {documento.items.map((item, idx) => (
                                            <div key={idx} className="p-4 flex items-center justify-between">
                                                <p className="font-medium">{item.descripcion}</p>
                                                <p className="font-mono text-sm">
                                                    {item.cantidad} {item.unidad}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Documentos e Imágenes Adjuntas */}
                        {(documento.documento_adjunto_url || documento.imagenes_carga?.length > 0 || documento.documento_fma002_url) && (
                            <Card className="border-2">
                                <CardHeader className="card-industrial-header">
                                    <CardTitle className="flex items-center gap-2">
                                        <ImageIcon className="h-5 w-5" />
                                        Documentos e Imágenes
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="p-4 space-y-4">
                                    {/* Documento del Folio Físico */}
                                    {documento.documento_adjunto_url && (
                                        <div className="space-y-2">
                                            <p className="text-xs text-muted-foreground uppercase tracking-wider">Documento del Folio Físico</p>
                                            <div className="flex items-center gap-2 flex-wrap">
                                                {documento.documento_adjunto_url.match(/\.(jpg|jpeg|png|webp|gif)$/i) ? (
                                                    <div 
                                                        className="relative w-24 h-24 rounded-lg overflow-hidden border cursor-pointer hover:ring-2 ring-primary transition-all"
                                                        onClick={() => setPreviewImage(documento.documento_adjunto_url)}
                                                    >
                                                        <img 
                                                            src={documento.documento_adjunto_url} 
                                                            alt="Documento folio" 
                                                            className="w-full h-full object-cover"
                                                        />
                                                        <div className="absolute inset-0 bg-black/0 hover:bg-black/20 flex items-center justify-center transition-colors">
                                                            <Eye className="h-6 w-6 text-white opacity-0 hover:opacity-100" />
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div
                                                        className="flex items-center gap-2 p-3 bg-muted rounded-lg cursor-pointer hover:ring-2 ring-primary transition-all"
                                                        onClick={() => setPreviewImage(documento.documento_adjunto_url)}
                                                    >
                                                        <FileImage className="h-5 w-5 text-primary" />
                                                        <span className="text-sm">{documento.documento_adjunto_url.match(/\.pdf$/i) ? 'PDF adjunto' : 'Documento adjunto'}</span>
                                                    </div>
                                                )}
                                                <div className="flex gap-2">
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => setPreviewImage(documento.documento_adjunto_url)}
                                                        data-testid="btn-preview-adjunto"
                                                    >
                                                        <Eye className="h-4 w-4 mr-1" />
                                                        Ver
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        asChild
                                                    >
                                                        <a href={documento.documento_adjunto_url} download target="_blank" rel="noopener noreferrer">
                                                            <Download className="h-4 w-4 mr-1" />
                                                            Descargar
                                                        </a>
                                                    </Button>
                                                </div>
                                            </div>

                                            {/* Documento firmado por aprobador (debajo del original) */}
                                            {documento.documento_firma_aprobador_url ? (
                                                <div className="mt-3 pl-4 border-l-4 border-success/60 space-y-2" data-testid="firma-aprobador-block">
                                                    <p className="text-xs text-success font-bold uppercase tracking-wider flex items-center gap-1">
                                                        <CheckCircle className="h-3 w-3" /> Documento firmado por aprobador
                                                    </p>
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <div
                                                            className="flex items-center gap-2 p-3 bg-success/10 rounded-lg cursor-pointer hover:ring-2 ring-success transition-all"
                                                            onClick={() => setPreviewImage(documento.documento_firma_aprobador_url)}
                                                        >
                                                            <FileImage className="h-5 w-5 text-success" />
                                                            <span className="text-sm">Documento firmado</span>
                                                        </div>
                                                        <Button size="sm" variant="outline" onClick={() => setPreviewImage(documento.documento_firma_aprobador_url)}>
                                                            <Eye className="h-4 w-4 mr-1" /> Ver
                                                        </Button>
                                                        <Button size="sm" variant="outline" asChild>
                                                            <a href={documento.documento_firma_aprobador_url} download target="_blank" rel="noopener noreferrer">
                                                                <Download className="h-4 w-4 mr-1" /> Descargar
                                                            </a>
                                                        </Button>
                                                        {puedeSubirFirma && (
                                                            <Button size="sm" variant="ghost" onClick={() => fileInputRef.current?.click()} disabled={uploadingFirma}>
                                                                <Upload className="h-4 w-4 mr-1" /> Reemplazar
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                            ) : puedeSubirFirma && (
                                                <div className="mt-3 pl-4 border-l-4 border-dashed border-amber-500/40">
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => fileInputRef.current?.click()}
                                                        disabled={uploadingFirma}
                                                        className="border-amber-500/60 text-amber-700 hover:bg-amber-500/10"
                                                        data-testid="btn-subir-firma-aprobador"
                                                    >
                                                        {uploadingFirma ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
                                                        Adjuntar documento firmado
                                                    </Button>
                                                    <p className="text-xs text-muted-foreground mt-1">PDF o imagen del documento firmado por el aprobador.</p>
                                                </div>
                                            )}
                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                accept="image/*,application/pdf"
                                                className="hidden"
                                                onChange={handleSubirFirma}
                                            />
                                        </div>
                                    )}

                                    {/* F-MA-002 */}
                                    {documento.documento_fma002_url && (
                                        <div className="space-y-2 pt-3 border-t">
                                            <p className="text-xs text-muted-foreground uppercase tracking-wider">Documento F-MA-002 (Residuos no peligrosos)</p>
                                            <div className="flex items-center gap-2">
                                                <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                                                    <FileImage className="h-5 w-5 text-blue-600" />
                                                    <span className="text-sm">F-MA-002</span>
                                                </div>
                                                <div className="flex gap-2">
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => setPreviewImage(documento.documento_fma002_url)}
                                                    >
                                                        <Eye className="h-4 w-4 mr-1" />
                                                        Ver
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        asChild
                                                    >
                                                        <a href={documento.documento_fma002_url} download target="_blank" rel="noopener noreferrer">
                                                            <Download className="h-4 w-4 mr-1" />
                                                            Descargar
                                                        </a>
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Imágenes de Carga */}
                                    {documento.imagenes_carga && documento.imagenes_carga.length > 0 && (
                                        <div className="space-y-2 pt-3 border-t">
                                            <p className="text-xs text-muted-foreground uppercase tracking-wider">
                                                Fotos de la Carga ({documento.imagenes_carga.length})
                                            </p>
                                            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                                                {documento.imagenes_carga.map((url, idx) => (
                                                    <div 
                                                        key={idx}
                                                        className="relative aspect-square rounded-lg overflow-hidden border cursor-pointer hover:ring-2 ring-primary transition-all group"
                                                        onClick={() => setPreviewImage(url)}
                                                    >
                                                        <img 
                                                            src={url} 
                                                            alt={`Carga ${idx + 1}`} 
                                                            className="w-full h-full object-cover"
                                                        />
                                                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 flex items-center justify-center transition-colors">
                                                            <Eye className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        )}

                        {/* Approval Info */}
                        {documento.aprobacion && (
                            <Card className={`border-2 ${documento.aprobacion.estado === 'autorizado' ? 'border-success/50' : 'border-destructive/50'}`}>
                                <CardHeader className="card-industrial-header">
                                    <CardTitle className="flex items-center gap-2">
                                        {documento.aprobacion.estado === 'autorizado' ? (
                                            <CheckCircle className="h-5 w-5 text-success" />
                                        ) : (
                                            <XCircle className="h-5 w-5 text-destructive" />
                                        )}
                                        Aprobación Gerencia
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="p-6">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Aprobador</p>
                                            <p className="font-medium">{documento.aprobacion.aprobador_nombre}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Fecha</p>
                                            <p className="font-medium">{formatDate(documento.aprobacion.fecha)}</p>
                                        </div>
                                    </div>
                                    {documento.aprobacion.observacion && (
                                        <div className="mt-4 pt-4 border-t border-border">
                                            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Observación</p>
                                            <p className="text-sm">{documento.aprobacion.observacion}</p>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        )}

                        {/* Security Checkpoint Info */}
                        {documento.punto_salida && (
                            <Card className={`border-2 ${documento.punto_salida.estado === 'despachado' ? 'border-success/50' : 'border-destructive/50'}`}>
                                <CardHeader className="card-industrial-header">
                                    <CardTitle className="flex items-center gap-2">
                                        {documento.punto_salida.estado === 'despachado' ? (
                                            <Truck className="h-5 w-5 text-success" />
                                        ) : (
                                            <AlertTriangle className="h-5 w-5 text-destructive" />
                                        )}
                                        Punto de Salida
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="p-6">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Responsable</p>
                                            <p className="font-medium">{documento.punto_salida.responsable_nombre}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Fecha Verificación</p>
                                            <p className="font-medium">{formatDate(documento.punto_salida.fecha_verificacion)}</p>
                                        </div>
                                    </div>
                                    {documento.punto_salida.motivos_rechazo && documento.punto_salida.motivos_rechazo.length > 0 && (
                                        <div className="mt-4 pt-4 border-t border-border">
                                            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Motivos de Rechazo</p>
                                            <ul className="space-y-1">
                                                {documento.punto_salida.motivos_rechazo.map((motivo, idx) => (
                                                    <li key={idx} className="text-sm text-destructive">
                                                        • {motivo}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {documento.punto_salida.observacion && (
                                        <div className="mt-4 pt-4 border-t border-border">
                                            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Observación</p>
                                            <p className="text-sm">{documento.punto_salida.observacion}</p>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        )}
                    </div>

                    {/* Sidebar: QR + Timeline */}
                    <div className="lg:col-span-1 space-y-6">
                        {/* QR Code */}
                        <Card className="border-2">
                            <CardHeader className="card-industrial-header">
                                <CardTitle className="flex items-center gap-2">
                                    <QrCode className="h-5 w-5" />
                                    Código QR
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-6 flex flex-col items-center">
                                <div className="bg-white p-4 rounded-sm">
                                    <QRCodeSVG
                                        value={`${window.location.origin}/qr/${documento.id}`}
                                        size={160}
                                        level="M"
                                        includeMargin={false}
                                    />
                                </div>
                                <p className="text-xs text-muted-foreground mt-3 text-center">
                                    Escanea para verificar y despachar
                                </p>
                            </CardContent>
                        </Card>

                        {/* Timeline */}
                        <Card className="border-2 sticky top-20">
                            <CardHeader className="card-industrial-header">
                                <CardTitle className="flex items-center gap-2">
                                    <History className="h-5 w-5" />
                                    Historial
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-4">
                                <ScrollArea className="h-[300px] pr-4">
                                    <div className="space-y-1">
                                        {historial.length === 0 ? (
                                            <p className="text-sm text-muted-foreground text-center py-4">
                                                Sin registros de historial
                                            </p>
                                        ) : (
                                            historial.map((item, idx) => (
                                                <div key={item.id} className="timeline-item">
                                                    <div className="timeline-dot border-primary">
                                                        {getHistoryIcon(item.accion)}
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-sm">{item.accion}</p>
                                                        <p className="text-xs text-muted-foreground">
                                                            {item.usuario_nombre}
                                                        </p>
                                                        <p className="text-xs text-muted-foreground">
                                                            {formatDate(item.created_at)}
                                                        </p>
                                                        {item.detalles?.observacion && (
                                                            <p className="text-xs mt-1 italic">
                                                                &ldquo;{item.detalles.observacion}&rdquo;
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </ScrollArea>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>

            {/* Preview Dialog: soporta imagen y PDF */}
            <Dialog open={!!previewImage} onOpenChange={() => setPreviewImage(null)}>
                <DialogContent className="max-w-5xl max-h-[92vh] p-0 overflow-hidden">
                    <div className="relative w-full h-full flex items-center justify-center bg-black/95">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="absolute top-2 right-2 text-white hover:bg-white/20 z-10"
                            onClick={() => setPreviewImage(null)}
                        >
                            <X className="h-6 w-6" />
                        </Button>
                        {previewImage && (previewImage.match(/\.pdf$/i) ? (
                            <iframe
                                src={previewImage}
                                title="Preview PDF"
                                className="w-full h-[88vh] bg-white"
                            />
                        ) : (
                            <img
                                src={previewImage}
                                alt="Preview"
                                className="max-w-full max-h-[88vh] object-contain"
                            />
                        ))}
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                            <Button size="sm" variant="secondary" asChild>
                                <a href={previewImage} download target="_blank" rel="noopener noreferrer">
                                    <Download className="h-4 w-4 mr-1" />
                                    Descargar
                                </a>
                            </Button>
                            <Button size="sm" variant="secondary" asChild>
                                <a href={previewImage} target="_blank" rel="noopener noreferrer">
                                    <Eye className="h-4 w-4 mr-1" />
                                    Abrir en nueva pestaña
                                </a>
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Diálogo de acción (aprobar/rechazar/despachar) */}
            <Dialog open={!!actionType} onOpenChange={v => !v && closeAccion()}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>
                            {actionType === 'aprobar' && 'Aprobar guía'}
                            {actionType === 'rechazar_gerente' && 'Rechazar guía (aprobación)'}
                            {actionType === 'despachar' && 'Despachar guía (P.I.)'}
                            {actionType === 'rechazar_pi' && 'Rechazar guía en P.I.'}
                        </DialogTitle>
                        <DialogDescription>
                            Folio: <span className="font-mono font-bold">{documento?.folio}</span>
                            {documento?.folio_fisico && (<span> · N° guía/factura: <span className="font-mono font-bold">{documento.folio_fisico}</span></span>)}
                        </DialogDescription>
                    </DialogHeader>

                    {actionType === 'rechazar_pi' && (
                        <div className="space-y-2">
                            <Label>Motivos de rechazo *</Label>
                            <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto border rounded p-2">
                                {MOTIVOS_RECHAZO_PI.map(m => (
                                    <label key={m} className="flex items-center gap-2 cursor-pointer text-sm hover:bg-muted/50 p-1 rounded">
                                        <Checkbox checked={motivosRechazo.includes(m)} onCheckedChange={() => toggleMotivo(m)} />
                                        {m}
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="space-y-2">
                        <Label>
                            Observación {(actionType === 'rechazar_gerente' || actionType === 'rechazar_pi') && <span className="text-destructive">*</span>}
                        </Label>
                        <Textarea
                            value={observacion}
                            onChange={e => setObservacion(e.target.value)}
                            placeholder={
                                actionType?.startsWith('rechazar') ? 'Indique el motivo del rechazo…' :
                                actionType === 'aprobar' ? 'Comentarios opcionales…' :
                                'Observaciones del despacho…'
                            }
                            rows={4}
                            data-testid="textarea-observacion-accion"
                        />
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={closeAccion} disabled={processing}>Cancelar</Button>
                        <Button
                            onClick={ejecutarAccion}
                            disabled={processing}
                            className={actionType?.startsWith('rechazar') ? 'bg-destructive hover:bg-destructive/90' : 'bg-success hover:bg-success/90'}
                            data-testid="btn-confirmar-accion"
                        >
                            {processing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                            Confirmar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}
